import { useTranslation } from "react-i18next";
import { ClipboardCheck, AlertTriangle, ShieldCheck } from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";
import { IndustrialFeatureCard } from "./IndustrialFeatureCard";
import { scrollToScreenshotSlide, type ScreenshotSlideKey } from "./ScreenshotShowcase";

/**
 * Industrial-DS Feature-Grid (TES-967, D5) — 3 flache outline-variant-Cards.
 * REALE CheckMaty-Wertversprechen (Sprint-1-Stripdown: KEIN SAP/AI als Feature):
 *  - Prüf-/Inspektionsmanagement
 *  - Mängel-/Qualitätsmanagement
 *  - Audit/Compliance + RBAC/Teams
 *
 * CTA-Verdrahtung (TES-1072): Jeder Karten-CTA scrollt zur "App in Aktion"-Galerie
 * und aktiviert den thematisch passenden Slide (keine eigenen Unterseiten).
 */
export function FeatureGrid() {
  const { t } = useTranslation();

  const features: Array<{
    icon: typeof ClipboardCheck;
    title: string;
    description: string;
    link: string;
    inverted: boolean;
    slide: ScreenshotSlideKey;
  }> = [
    {
      icon: ClipboardCheck,
      title: t("landing.industrial.features.inspection.title"),
      description: t("landing.industrial.features.inspection.description"),
      link: t("landing.industrial.features.inspection.link"),
      inverted: false,
      slide: "inspections",
    },
    {
      icon: AlertTriangle,
      title: t("landing.industrial.features.defects.title"),
      description: t("landing.industrial.features.defects.description"),
      link: t("landing.industrial.features.defects.link"),
      inverted: true,
      slide: "defects",
    },
    {
      icon: ShieldCheck,
      title: t("landing.industrial.features.compliance.title"),
      description: t("landing.industrial.features.compliance.description"),
      link: t("landing.industrial.features.compliance.link"),
      inverted: false,
      // Kein eigener Audit-Slide vorhanden — Checklisten sind die Standards-Ansicht.
      slide: "checklists",
    },
  ];

  return (
    <section className="bg-surface py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div className="space-y-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-secondary-foreground">
                {t("landing.industrial.features.eyebrow")}
              </span>
              <h2 className="max-w-md font-display text-3xl font-bold tracking-display text-primary sm:text-4xl">
                {t("landing.industrial.features.heading")}
              </h2>
            </div>
            <p className="max-w-xs border-l-2 border-secondary pl-6 text-muted-foreground">
              {t("landing.industrial.features.lead")}
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {features.map((f, i) => (
            <ScrollReveal key={f.title} delay={i * 100}>
              <IndustrialFeatureCard
                icon={f.icon}
                title={f.title}
                description={f.description}
                link={f.link}
                inverted={f.inverted}
                onLinkClick={() => scrollToScreenshotSlide(f.slide)}
                linkTestId={`feature-cta-${f.slide}`}
              />
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
